// src/pages/MarketMap/index.jsx

import React, { useEffect, useMemo, useState } from "react";
import useMarketSummary from "./useMarketSummary";
import useMarketMapPreferences from "./useMarketMapPreferences";
import KpiCard from "./KpiCard";
import CommodityStripsCard from "./CommodityStripsCard";
import HistoricalChartPanel from "./HistoricalChartPanel";
import SectionCard from "components/SectionCard";
import Page from "components/Page";
import { supabase } from "services/supabaseClient";
import { normalizeValue } from "lib/marketFormat";
import CurveChart from "./CurveChart";
import { buildChartData } from "utils/buildChartData";
import { fmtISODate, parseISODateLocal } from "pages/DebtStudio/lib/dateUtils";
import { useProfile } from "context/ProfileContext";

// icons
import { Landmark, ChartBar, Fuel, Leaf, PiggyBank } from "lucide-react";

const FAVORITE_CATEGORY = "market_map.favorite_security";
const VIEW_MODE_KEY = "market-map.view-mode";

const kpiDefaults = {
  rates: [
    { symbol: "ust-1m", icon: Landmark },
    { symbol: "ust-3m", icon: Landmark },
    { symbol: "ust-6m", icon: Landmark },
    { symbol: "ust-10y", icon: Landmark },
    { symbol: "ust-30y", icon: Landmark },
    { symbol: "fed-funds-eff", icon: Landmark },
    { symbol: "mmf-govt-jpm", icon: PiggyBank },
    { symbol: "sofr-daily", icon: ChartBar },
    { symbol: "sofr-cme-1m", icon: ChartBar },
    { symbol: "sofr-cme-3m", icon: ChartBar },
    { symbol: "muni-sifma", icon: ChartBar },
  ],
  commodities: [
    { symbol: "oil-wti-active", icon: Fuel },
    { symbol: "ng-nymex-active", icon: Leaf },
    { symbol: "ho-nymex-active", key: "ulsd", icon: Fuel },
    { symbol: "rbob-nymex-active", icon: Fuel },
  ],
};

export default function MarketMap() {
  const { profile } = useProfile();
  const {
    loading,
    latestMap,
    lastAsOf,
    distinctSymbols,
    distinctTenors,
    rowCount,
  } = useMarketSummary();
  const {
    loading: preferencesLoading,
    error: preferencesError,
    getPreference,
    setPreference,
    removePreference,
  } = useMarketMapPreferences(profile?.id);

  const [viewMode, setViewMode] = useState(() =>
    window.localStorage.getItem(VIEW_MODE_KEY) === "favorites" ? "favorites" : "all"
  );
  const [updatingFavorites, setUpdatingFavorites] = useState(() => new Set());

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  const [showTaxable, setShowTaxable] = useState(true);
  const [showSwaps, setShowSwaps] = useState(true);
  const [showTaxExempt, setShowTaxExempt] = useState(true);
  const [showNG, setShowNG] = useState(true);
  const [showULSD, setShowULSD] = useState(true);
  const [showRBOB, setShowRBOB] = useState(true);

  // Robust lookup helper
  function findLatest(symbol) {
    if (!latestMap || typeof latestMap !== "object" || !symbol) return null;
    if (Object.prototype.hasOwnProperty.call(latestMap, symbol))
      return latestMap[symbol];
    const up = symbol.toUpperCase();
    if (Object.prototype.hasOwnProperty.call(latestMap, up)) return latestMap[up];
    const low = symbol.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(latestMap, low))
      return latestMap[low];
    const lowerSym = String(symbol || "").toLowerCase();
    const keys = Object.keys(latestMap || {});
    const containsKey = keys.find((k) => k && k.toLowerCase().includes(lowerSym));
    if (containsKey) return latestMap[containsKey];
    const reverseKey = keys.find((k) => k && lowerSym.includes(k.toLowerCase()));
    if (reverseKey) return latestMap[reverseKey];
    return null;
  }

  // KPIs
  const ratesKpis = useMemo(() => {
    return kpiDefaults.rates.map((k) => {
      const fromMap = findLatest(k.symbol);
      return {
        symbol: k.symbol,
        label: fromMap?.label || fromMap?.raw?.ticker || k.symbol,
        displayName: fromMap?.raw?.name || fromMap?.label || k.symbol,
        value: normalizeValue(k.symbol, fromMap?.val),
        suffix: fromMap?.raw?.units ?? "",
        icon: k.icon ?? Landmark,
      };
    });
  }, [latestMap]);

  const commodityKpis = useMemo(() => {
    return kpiDefaults.commodities.map((k) => {
      const fromMap = findLatest(k.symbol);
      return {
        symbol: k.symbol,
        label: fromMap?.label || fromMap?.raw?.ticker || k.symbol,
        displayName: fromMap?.raw?.name || fromMap?.label || k.symbol,
        value: normalizeValue(k.symbol, fromMap?.val),
        suffix: fromMap?.raw?.units ?? "",
        icon: k.icon ?? Leaf,
      };
    });
  }, [latestMap]);

  const visibleRatesKpis = useMemo(
    () =>
      viewMode === "favorites"
        ? ratesKpis.filter((k) => getPreference(FAVORITE_CATEGORY, k.symbol) === true)
        : ratesKpis,
    [getPreference, ratesKpis, viewMode]
  );

  const visibleCommodityKpis = useMemo(
    () =>
      viewMode === "favorites"
        ? commodityKpis.filter((k) => getPreference(FAVORITE_CATEGORY, k.symbol) === true)
        : commodityKpis,
    [commodityKpis, getPreference, viewMode]
  );

  async function toggleFavorite(symbol) {
    if (!profile?.id || updatingFavorites.has(symbol)) return;

    setUpdatingFavorites((current) => new Set(current).add(symbol));
    try {
      if (getPreference(FAVORITE_CATEGORY, symbol) === true) {
        await removePreference(FAVORITE_CATEGORY, symbol);
      } else {
        await setPreference(FAVORITE_CATEGORY, symbol, true);
      }
    } catch (error) {
      console.error("[MarketMap] favorite update error:", error);
    } finally {
      setUpdatingFavorites((current) => {
        const next = new Set(current);
        next.delete(symbol);
        return next;
      });
    }
  }

  // Yield Curve Data
  const yieldCurveTenors = ["1m","3m", "6m", "1y", "2y", "3y", "5y", "7y", "10y", "20y", "30y"];
  const yieldCurveSeries = [
    { key: "UST", prefix: "ust-" },
    { key: "SOFR", prefix: "sofr-swap" },
    { key: "Muni-AAA", prefix: "muni-aaa-go-" },
    { key: "Muni-A", prefix: "muni-a-go-" },
  ];
  const yieldCurveData = useMemo(
    () =>
      buildChartData({
        latestMap,
        seriesDefs: yieldCurveSeries,
        tenorList: yieldCurveTenors,
      }),
    [latestMap]
  );

  // Commodity Futures Data
  const futTenors = Array.from({ length: 36 }, (_, i) => `${i + 1}m`);
  const futSeries = [
    { key: "ng", prefix: "ng-nymex-fut-" },
    { key: "ng_fgtz3", prefix: "ng-fgtz3-fut-" },
    { key: "ulsd", prefix: "ho-nymex-fut-", divisor: 100 },
    { key: "rbob", prefix: "rbob-nymex-fut-", divisor: 100 },
  ];
  const futData = useMemo(
    () =>
      buildChartData({
        latestMap,
        seriesDefs: futSeries,
        tenorList: futTenors,
      }),
    [latestMap]
  );

  // History modal state
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [selectedDisplayName, setSelectedDisplayName] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedSeriesData, setSelectedSeriesData] = useState([]);

  async function openSeries(symbol, displayName) {
    setSelectedSymbol(symbol);
    setSelectedDisplayName(displayName || symbol);
    setSelectedLoading(true);
    setSelectedSeriesData([]);
    try {
      const { data, error: sbErr } = await supabase
        .from("market_data")
        .select("*")
        .or(`security_id.eq.${symbol},ticker.eq.${symbol},name.eq.${symbol}`)
        .order("date", { ascending: true })
        .limit(20000);

      if (sbErr) throw sbErr;

      if (!data || !data.length) {
        setSelectedSeriesData([]);
        setSelectedLoading(false);
        return;
      }

      const parsed = data
        .map((r) => {
          const dateKey = Object.keys(r).find((k) =>
            ["date", "data_date", "as_of", "load_timestamp", "timestamp", "created_at"].includes(k)
          );
          const valueKey = Object.keys(r).find((k) =>
            ["value", "price", "mid", "close", "val"].includes(k)
          );
          const dRaw = dateKey ? r[dateKey] : null;
          const v = valueKey ? Number(String(r[valueKey]).replace(/,/g, "")) : null;
          const dStr = dRaw ? fmtISODate(parseISODateLocal(String(dRaw).slice(0, 10))) : null;
          return {
            date: dStr,
            label: dStr,
            value: Number.isFinite(v) ? v : null,
            raw: r,
          };
        })
        .filter((x) => x.value !== null);

      setSelectedSeriesData(parsed);
    } catch (err) {
      console.error("[MarketMap] openSeries error:", err);
    } finally {
      setSelectedLoading(false);
    }
  }

  function closeSeries() {
    setSelectedSymbol(null);
    setSelectedDisplayName(null);
    setSelectedSeriesData([]);
    setSelectedLoading(false);
  }

  // asOfDisplay with ISO format
  const asOfDisplay = lastAsOf
    ? typeof lastAsOf === "string"
      ? fmtISODate(parseISODateLocal(lastAsOf))
      : fmtISODate(new Date(lastAsOf))
    : loading
    ? "loading…"
    : "no data";

  return (
    <Page
      title="MarketMap"
      right={
        <div className="inline-flex rounded-md border border-border bg-app p-0.5" aria-label="KPI view">
          {[
            { value: "all", label: "All" },
            { value: "favorites", label: "Favorites" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setViewMode(option.value)}
              disabled={option.value === "favorites" && preferencesLoading}
              aria-pressed={viewMode === option.value}
              className={`rounded px-3 py-1 text-sm font-medium transition-colors disabled:cursor-wait disabled:opacity-50 ${
                viewMode === option.value
                  ? "bg-brand1 text-brand1-foreground shadow-sm"
                  : "text-muted hover:text-app"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      }
    >
      {preferencesError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          Favorites could not be loaded or saved. Please try again.
        </div>
      ) : null}
      {/* Rates Section */}
      <SectionCard title="Interest Rates">
        <div className="flex items-center gap-1 mt-1">
          <div className="text-sm text-muted">Market data as of</div>
          <div className="text-sm font-medium">{asOfDisplay}</div>
          <div className="text-sm font-medium">end of day</div>
        </div>

        <div className="space-y-4 mt-3">
          {/* KPI grid */}
          <div className="grid grid-cols-[repeat(auto-fill,174px)] gap-2 justify-start">
            {visibleRatesKpis.map((k) => (
              <KpiCard
                key={k.symbol}
                displayName={k.displayName}
                label={k.label}
                value={k.value}
                suffix={k.suffix}
                Icon={k.icon}
                onClick={() => openSeries(k.symbol, k.displayName)}
                isFavorite={getPreference(FAVORITE_CATEGORY, k.symbol) === true}
                onToggleFavorite={() => toggleFavorite(k.symbol)}
                favoriteDisabled={preferencesLoading || updatingFavorites.has(k.symbol)}
              />
            ))}
            {viewMode === "favorites" && !visibleRatesKpis.length ? (
              <div className="col-span-full py-3 text-sm text-muted">
                {preferencesLoading
                  ? "Loading favorite interest-rate series…"
                  : "No favorite interest-rate series yet."}
              </div>
            ) : null}
          </div>

          {/* Yield curve */}
          {yieldCurveData && yieldCurveData.length ? (
            <CurveChart
              title="Yield Curves"
              data={yieldCurveData}
              latestMap={latestMap}
              series={[
                { key: "UST", label: "US Treasury", color: "var(--chart-1)" },
                { key: "SOFR", label: "SOFR Swaps", color: "var(--chart-2)" },
                { key: "Muni-AAA", label: "Muni AAA GO", color: "var(--chart-3)" },
                { key: "Muni-A", label: "Muni A GO", color: "var(--chart-4)" },
              ]}
              visible={{
                UST: showTaxable,
                SOFR: showSwaps,
                "Muni-AAA": showTaxExempt,
                "Muni-A": showTaxExempt,
              }}
              yFormatter={(v) => `${v.toFixed(2)}%`}
              tooltipFormatter={(v, name) => [`${(+v).toFixed(2)}%`, name]}
              checkboxes={
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTaxable}
                      onChange={() => setShowTaxable((v) => !v)}
                    />
                    US Treasury
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTaxExempt}
                      onChange={() => setShowTaxExempt((v) => !v)}
                    />
                    Muni Tax-Exempt
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showSwaps}
                      onChange={() => setShowSwaps((v) => !v)}
                    />
                    SOFR Swaps
                  </label>
                </div>
              }
            />
          ) : (
            <div className="text-xs text-muted py-6 text-center">
              No yield curve available for the selected date.
            </div>
          )}
        </div>
      </SectionCard>

      {/* Commodities Section */}
      <SectionCard title="Energy Commodities">
        <div className="flex items-center gap-1 mt-1">
          <div className="text-sm text-muted">Market data as of</div>
          <div className="text-sm font-medium">{asOfDisplay}</div>
          <div className="text-sm font-medium">end of day</div>
        </div>

        <div className="space-y-4 mt-3">
          {/* KPI grid */}
          <div className="grid grid-cols-[repeat(auto-fill,174px)] gap-2 justify-start">
            {visibleCommodityKpis.map((k) => (
              <KpiCard
                key={k.symbol}
                displayName={k.displayName}
                label={k.label}
                value={k.value}
                suffix={k.suffix}
                Icon={k.icon}
                onClick={() => openSeries(k.symbol, k.displayName)}
                isFavorite={getPreference(FAVORITE_CATEGORY, k.symbol) === true}
                onToggleFavorite={() => toggleFavorite(k.symbol)}
                favoriteDisabled={preferencesLoading || updatingFavorites.has(k.symbol)}
              />
            ))}
            {viewMode === "favorites" && !visibleCommodityKpis.length ? (
              <div className="col-span-full py-3 text-sm text-muted">
                {preferencesLoading
                  ? "Loading favorite commodity series…"
                  : "No favorite commodity series yet."}
              </div>
            ) : null}
          </div>

          <CommodityStripsCard
            latestMap={latestMap}
            labels={{
              ng: "Nat Gas NYMEX Henry Hub",
              ng_fgtz3: "Nat Gas FGT Zone 3",
              rbob: "Gasoline NYMEX RBOB",
              ulsd: "Diesel NYMEX ULSD",
            }}
          />

          {/* Futures chart */}
          <CurveChart
            title="Futures/Forward Prices"
            data={futData}
            latestMap={latestMap}
            series={[
              { key: "ng", label: "NG NYMEX Henry Hub", color: "var(--chart-1)" },
              { key: "ng_fgtz3", label: "NG FGT Zone 3", color: "var(--chart-2)" },
              { key: "ulsd", label: "Diesel NYMEX ULSD", color: "var(--chart-4)" },
              { key: "rbob", label: "Gasoline NYMEX RBOB", color: "var(--chart-3)" },
            ]}
            visible={{
              ng: showNG,
              ng_fgtz3: showNG,
              ulsd: showULSD,
              rbob: showRBOB,
            }}
            yFormatter={(v) => `$${v.toFixed(2)}`}
            tooltipFormatter={(v, name) => [`$${(+v).toFixed(2)}`, name]}
            height="h-80"
            checkboxes={
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showNG}
                    onChange={() => setShowNG((v) => !v)}
                  />
                  Natural Gas
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showULSD}
                    onChange={() => setShowULSD((v) => !v)}
                  />
                  Diesel
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showRBOB}
                    onChange={() => setShowRBOB((v) => !v)}
                  />
                  Gasoline
                </label>
              </div>
            }
          />
        </div>
      </SectionCard>

      {/* Diagnostics */}
      {rowCount === 0 && (
        <div className="pt-3">
          <div className="text-sm font-medium">Diagnostics</div>
          <div className="text-xs text-muted">Row count: {rowCount}</div>
          <div className="text-xs text-muted">
            Distinct symbols (sample): {distinctSymbols.slice(0, 10).join(", ") || "—"}
          </div>
          <div className="text-xs text-muted">
            Distinct tenors (sample): {distinctTenors.slice(0, 10).join(", ") || "—"}
          </div>
        </div>
      )}

      {/* Chart modal */}
      <HistoricalChartPanel
        open={!!selectedSymbol}
        symbol={selectedSymbol}
        displayName={selectedDisplayName}
        loading={selectedLoading}
        data={selectedSeriesData}
        onClose={closeSeries}
      />
    </Page>
  );
}
